import React, { useEffect, useRef, useState } from 'react';
import { useReadOnly } from '@spark';
import NerExampleEditor from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/NerExampleEditor';
import EntityTagLegend from '@/features/datasets/components/DataSelection/EntityTagLegend';
import NerTagDeleteConfirmModal from '@/features/datasets/components/DataSelection/NerTagDeleteConfirmModal';
import useNerTagCatalog from '@/features/datasets/hooks/useNerTagCatalog';
import useNerAnnotationAutoSave from '@/features/datasets/hooks/useNerAnnotationAutoSave';
import { toEditorEntities } from '@/features/datasets/utils/nerAnnotationEntities';
import { removeEntityAt } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/nerExampleSpans';
import { ActivityType } from '@/shared/domain/taskTypes';
import { useAuth } from '@/features/auth/AuthContext';

const draftFromRecord = (record) => ({
    text: record?.request_example || '',
    entities: toEditorEntities(record?.request_example || '', record?.response?.entities || []),
});

const spanKey = (entity) => `${entity.start}:${entity.end}:${entity.type}`;

/**
 * Editable annotate-modal body for plain NER records.
 *
 * Reuses the `NerExampleEditor` from the data-collection flows: the example
 * text is fully editable (entity offsets are recomputed on every edit) and
 * spans are tagged by highlighting text or clicking a word, then picking an
 * entity type from the popover. The popover also exposes Validate/Unvalidate
 * and Remove-tag actions for existing entities.
 *
 * Changes are autosaved: typing is debounced (700ms, like the prompt
 * autosave), discrete entity actions save immediately with the matching
 * activity type. The "Available tags" legend keeps its tag-management flows
 * (+ New Tag / delete tag) via `useNerTagCatalog`.
 */
export default function NerAnnotateEditor({
    record,
    entityOptions,
    datasetName,
    refreshCategories,
    updateRecord,
    fetchValidationCounts,
    llm_usecase_id,
    isBenchmark,
    taskType,
    onRefreshRecords,
    onDataOverviewRefresh,
}) {
    const { role } = useAuth();
    const { readOnly } = useReadOnly();
    // Annotating (tagging spans, validating, editing the example text) is open
    // to every workspace role, Viewer included; only the tag catalog
    // (+ New Tag / delete tag) stays Editor-only. See docs/features/platform-roles.md.
    const disabled = readOnly;
    const canManageTags = !readOnly && role !== 'Viewer';
    const [draft, setDraft] = useState(() => draftFromRecord(record));

    const { commitChange, flushSave, resetBaseline } = useNerAnnotationAutoSave({
        record,
        datasetName,
        isBenchmark,
        llm_usecase_id,
        taskType,
        updateRecord,
        fetchValidationCounts,
        onDataOverviewRefresh,
        enabled: !disabled,
        initialDraft: draft,
    });

    // Re-init when the modal is reused for a different record.
    const recordIdRef = useRef(record?._id);
    useEffect(() => {
        if (record?._id && record._id !== recordIdRef.current) {
            recordIdRef.current = record._id;
            const next = draftFromRecord(record);
            setDraft(next);
            resetBaseline(next);
        }
    }, [record, resetBaseline]);

    const applyChange = (next, activity = null, immediate = false) => {
        setDraft(next);
        commitChange(next, activity, immediate);
    };

    const handleEditorChange = (next) => {
        if (next.text === draft.text) {
            // Entity-only change (tag added / re-tagged / untagged in the
            // editor): save immediately with the matching activity, like the
            // legacy tagger did.
            const prevKeys = new Set(draft.entities.map(spanKey));
            const nextKeys = new Set(next.entities.map(spanKey));
            const added = next.entities.find((entity) => !prevKeys.has(spanKey(entity)));
            const removed = draft.entities.find((entity) => !nextKeys.has(spanKey(entity)));
            if (added) {
                applyChange(
                    next,
                    {
                        type: ActivityType.entity_added,
                        comment: `entity: ${next.text.slice(added.start, added.end)}, label: ${added.type}`,
                    },
                    true
                );
                return;
            }
            if (removed) {
                applyChange(
                    next,
                    {
                        type: ActivityType.entity_deleted,
                        comment: `entity: ${draft.text.slice(removed.start, removed.end)}, label: ${removed.type}`,
                    },
                    true
                );
                return;
            }
            applyChange(next);
            return;
        }
        // Text edit — the editor already shifted/dropped entity offsets;
        // debounce the save while the user keeps typing.
        applyChange(next);
    };

    const findEntity = (start, end) =>
        draft.entities.find((entity) => entity.start === start && entity.end === end);

    const getEntityMeta = (start, end) => {
        const entity = findEntity(start, end);
        return entity ? { validated: entity.is_validated ?? true } : null;
    };

    const handleToggleEntityValidated = ({ start, end }) => {
        const entity = findEntity(start, end);
        if (!entity) return;
        const nextValidated = !(entity.is_validated ?? true);
        const next = {
            text: draft.text,
            entities: draft.entities.map((current) =>
                current === entity ? { ...current, is_validated: nextValidated } : current
            ),
        };
        applyChange(
            next,
            {
                type: nextValidated ? ActivityType.validated_entity : ActivityType.unvalidated_entity,
                comment: `entity: ${draft.text.slice(start, end)}, label: ${entity.type}`,
            },
            true
        );
    };

    const handleRemoveEntity = ({ start, end }) => {
        const entity = findEntity(start, end);
        if (!entity) return;
        const next = { text: draft.text, entities: removeEntityAt(draft.entities, start, end) };
        applyChange(
            next,
            {
                type: ActivityType.entity_deleted,
                comment: `entity: ${draft.text.slice(start, end)}, label: ${entity.type}`,
            },
            true
        );
    };

    const { allCategories, categoryCounts, addTag, requestDeleteTag, deleteConfirm } = useNerTagCatalog({
        entityOptions,
        entityLabels: draft.entities.map((entity) => entity.type),
        llm_usecase_id,
        dataset: datasetName,
        refreshCategories,
        fetchValidationCounts,
        onRefreshRecords,
        updateRecord,
        isBenchmark,
        recordId: record?._id,
        onCategoryDeleted: (cat, result) => {
            if (result?.new_record?._id) {
                // Server already removed the annotations and returned the
                // fresh record — adopt it as the new baseline.
                const next = draftFromRecord(result.new_record);
                setDraft(next);
                resetBaseline(next);
                return;
            }
            if (draft.entities.some((entity) => entity.type === cat)) {
                applyChange(
                    { text: draft.text, entities: draft.entities.filter((entity) => entity.type !== cat) },
                    { type: ActivityType.entity_deleted, comment: `category: ${cat}` },
                    true
                );
            }
        },
    });

    return (
        <div className="ner-annotate-editor">
            <NerExampleEditor
                placeholder="Type or paste example text"
                text={draft.text}
                entities={draft.entities}
                entityNames={allCategories}
                onChange={handleEditorChange}
                height={280}
                dir="auto"
                disabled={disabled}
                error={!draft.text.trim()}
                getEntityMeta={getEntityMeta}
                onToggleEntityValidated={disabled ? undefined : handleToggleEntityValidated}
                onRemoveEntity={disabled ? undefined : handleRemoveEntity}
            />
            <EntityTagLegend
                allCategories={allCategories}
                categoryCounts={categoryCounts}
                onDeleteCategory={(cat) => {
                    // Flush any pending text edits before the (server-side)
                    // category delete so they aren't lost when the modal
                    // resyncs from the returned record.
                    flushSave();
                    requestDeleteTag(cat);
                }}
                onAddTag={addTag}
                canDelete={canManageTags}
            />
            <NerTagDeleteConfirmModal {...deleteConfirm} />
        </div>
    );
}
